import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import styles from "./Alert.module.css";

export type AlertTone = "danger" | "warning" | "info" | "success";

export type AlertProps = Omit<HTMLAttributes<HTMLElement>, "title"> & {
  /** Défaut `danger` : un refus du service. `warning` pour un blocage qui s'explique et se lève. */
  tone?: AlertTone;
  /** Ce qui s'est passé, en une phrase. */
  title?: ReactNode;
  /** Pourquoi, et quoi faire. */
  children?: ReactNode;
  /** `p` ou `span` quand l'encadré tient dans un paragraphe ou une ligne. */
  as?: "div" | "p" | "span";
};

/**
 * Encadré de refus ou d'avertissement, posé à côté du geste qui l'a provoqué.
 *
 * Il existait en cinq copies, avec des tons différents pour le même cas : un
 * refus de commande en rouge, le même refus à l'achat en orange. Le ton dit
 * désormais la nature du message, pas l'écran qui l'affiche.
 *
 * Le `role` reste à l'appelant : `alert` pour un refus qui vient d'arriver,
 * `status` pour un blocage connu dès l'affichage.
 */
export function Alert({ tone = "danger", title, children, as = "div", className, ...rest }: AlertProps) {
  const Balise = as;
  // Dans un `p`, les sous-éléments ne peuvent pas être des `p` : on passe en `span`.
  const Ligne = as === "div" ? "p" : "span";
  return (
    <Balise className={cx(styles.encadre, styles[tone], className)} {...rest}>
      {title !== undefined && <Ligne className={styles.titre}>{title}</Ligne>}
      {children !== undefined && <Ligne className={styles.detail}>{children}</Ligne>}
    </Balise>
  );
}
